export const BOX_SIZE_OPPORTUNITY_RULES = {
  dimDivisor: 194,
  additionalHandling: {
    longestSide: 48,
    secondLongestSide: 30,
    cubicVolume: 10368,
  },
  largePackage: {
    longestSide: 96,
    lengthPlusGirth: 130,
    cubicVolume: 17280,
  },
  surePostDimThreshold: 1729,
};

export const UPS_SUREPOST_SERVICE_CODES = ['92', '93', '94', '95'];

export function isUpsCarrier(carrier) {
  return `${carrier || ''}`.trim().toUpperCase() === 'UPS';
}

export function isUpsSurePostService(service) {
  const normalizedService = `${service || ''}`.trim();
  const serviceName = normalizedService.toLowerCase();

  return UPS_SUREPOST_SERVICE_CODES.includes(normalizedService)
    || serviceName.includes('surepost')
    || serviceName.includes('ground saver');
}

function summarize(result) {
  return {
    dimensions: result.dimensions.join('x'),
    volume: result.volume,
    dimensionalWeight: result.dimensionalWeight,
    billableWeight: result.billableWeight,
    lengthPlusGirth: result.lengthPlusGirth,
    additionalHandling: result.additionalHandling,
    additionalHandlingReasons: result.additionalHandlingReasons,
    largePackage: result.largePackage,
    largePackageReasons: result.largePackageReasons,
    overSurePostDimThreshold: result.overSurePostDimThreshold,
  };
}

function getPriority(opportunity) {
  if (opportunity.benefits.some((benefit) => benefit.type === 'AVOIDS_LARGE_PACKAGE')) {
    return 100;
  }

  if (opportunity.benefits.some((benefit) => benefit.type === 'AVOIDS_ADDITIONAL_HANDLING')) {
    return 80;
  }

  if (opportunity.benefits.some((benefit) => benefit.type === 'SUREPOST_DIM_EXEMPT')) {
    return 70;
  }

  const dimWeightBenefit = opportunity.benefits.find((benefit) => benefit.type === 'DIM_WEIGHT');
  return dimWeightBenefit ? dimWeightBenefit.poundsSaved * 10 : 0;
}

export function getBoxSizeOpportunities(
  dimString,
  actualWeight,
  { carrier, service, rules = BOX_SIZE_OPPORTUNITY_RULES } = {},
) {
  if (typeof dimString !== 'string') {
    throw new Error('Dimensions must be a string like "12x10x7"');
  }

  const dimensions = dimString
    .toLowerCase()
    .replace(/\s/g, '')
    .split('x')
    .map(Number);

  if (dimensions.length !== 3 || dimensions.some((dimension) => (
    !Number.isFinite(dimension) || dimension <= 0
  ))) {
    throw new Error(`Invalid dimensions: "${dimString}"`);
  }

  const numericWeight = Number(actualWeight);
  if (!Number.isFinite(numericWeight) || numericWeight <= 0) {
    throw new Error('Weight must be a positive number');
  }

  if (!isUpsCarrier(carrier)) return null;

  const billingDimensions = dimensions.map(Math.ceil);
  const actualBillableWeight = Math.ceil(numericWeight);
  const includeSurePostOpportunity = isUpsSurePostService(service);

  function analyze(inputDimensions) {
    const sorted = [...inputDimensions].sort((a, b) => b - a);
    const [length, width, height] = sorted;
    const volume = length * width * height;
    const dimensionalWeight = Math.ceil(volume / rules.dimDivisor);
    const lengthPlusGirth = length + (2 * width) + (2 * height);
    const additionalHandlingReasons = [];
    const largePackageReasons = [];

    if (length > rules.additionalHandling.longestSide) {
      additionalHandlingReasons.push('LONGEST_SIDE');
    }
    if (width > rules.additionalHandling.secondLongestSide) {
      additionalHandlingReasons.push('SECOND_LONGEST_SIDE');
    }
    if (volume > rules.additionalHandling.cubicVolume) {
      additionalHandlingReasons.push('CUBIC_VOLUME');
    }

    if (length > rules.largePackage.longestSide) {
      largePackageReasons.push('LONGEST_SIDE');
    }
    if (lengthPlusGirth > rules.largePackage.lengthPlusGirth) {
      largePackageReasons.push('LENGTH_PLUS_GIRTH');
    }
    if (volume > rules.largePackage.cubicVolume) {
      largePackageReasons.push('CUBIC_VOLUME');
    }

    return {
      dimensions: sorted,
      volume,
      dimensionalWeight,
      billableWeight: Math.max(actualBillableWeight, dimensionalWeight),
      lengthPlusGirth,
      additionalHandling: additionalHandlingReasons.length > 0,
      additionalHandlingReasons,
      largePackage: largePackageReasons.length > 0,
      largePackageReasons,
      overSurePostDimThreshold: volume > rules.surePostDimThreshold,
    };
  }

  const current = analyze(billingDimensions);
  const opportunities = [];

  billingDimensions.forEach((dimension, dimensionIndex) => {
    if (dimension <= 1) return;

    const smallerDimensions = [...billingDimensions];
    smallerDimensions[dimensionIndex] -= 1;
    const candidate = analyze(smallerDimensions);
    const benefits = [];
    const billableWeightSavings = current.billableWeight - candidate.billableWeight;

    if (billableWeightSavings > 0) {
      benefits.push({
        type: 'DIM_WEIGHT',
        from: current.billableWeight,
        to: candidate.billableWeight,
        poundsSaved: billableWeightSavings,
      });
    }

    if (current.additionalHandling && !candidate.additionalHandling) {
      benefits.push({
        type: 'AVOIDS_ADDITIONAL_HANDLING',
        reasons: current.additionalHandlingReasons,
      });
    }

    if (current.largePackage && !candidate.largePackage) {
      benefits.push({
        type: 'AVOIDS_LARGE_PACKAGE',
        reasons: current.largePackageReasons,
      });
    }

    if (
      includeSurePostOpportunity
      && current.overSurePostDimThreshold
      && !candidate.overSurePostDimThreshold
    ) {
      benefits.push({
        type: 'SUREPOST_DIM_EXEMPT',
        fromVolume: current.volume,
        toVolume: candidate.volume,
        threshold: rules.surePostDimThreshold,
      });
    }

    if (benefits.length > 0) {
      opportunities.push({
        dimensionIndex,
        from: billingDimensions.join('x'),
        to: smallerDimensions.join('x'),
        changedDimension: {
          from: dimension,
          to: smallerDimensions[dimensionIndex],
        },
        volumeSavings: current.volume - candidate.volume,
        benefits,
        before: summarize(current),
        after: summarize(candidate),
      });
    }
  });

  opportunities.sort((a, b) => (
    getPriority(b) - getPriority(a)
    || b.volumeSavings - a.volumeSavings
    || a.dimensionIndex - b.dimensionIndex
  ));

  return {
    input: {
      dimensions: dimString,
      billingDimensions: billingDimensions.join('x'),
      actualWeight: numericWeight,
      carrier,
      service,
    },
    current: summarize(current),
    hasOpportunity: opportunities.length > 0,
    opportunities,
    bestOpportunity: opportunities[0] || null,
  };
}
